import { useMemo } from 'react';
import { capabilitiesFor, type Capability } from '@rundown/policy';
import { useWorkspace } from './useWorkspace';

/**
 * ─────────────────────────────────────────────────────────────────────────────
 * THIS IS A UI CONVENIENCE ONLY. IT IS NOT A SECURITY BOUNDARY.
 *
 * Everything these hooks decide is decided again, authoritatively, on the server
 * — the API guards each handler with the same capability from the same table in
 * @rundown/policy, and RLS sits under that. The role on the profile is data that
 * arrived over the wire in a browser the user controls; treating it as a gate
 * means a devtools edit hands someone `billing.read`.
 *
 * The rule: the UI hides what a user cannot do so the screen is honest. It never
 * *permits* anything. If a check here is the only thing standing between a user
 * and an action, that action has a server-side bug.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Checks are always by capability, never by role. `useCapability('billing.read')`
 * survives the role matrix changing; `role === 'vendor_admin'` does not.
 */

const NO_CAPABILITIES: ReadonlySet<Capability> = new Set<Capability>();

/** Every capability granted by the current workspace role. */
export function useCapabilities(): ReadonlySet<Capability> {
  const current = useWorkspace();
  const role = current?.role;

  // capabilitiesFor allocates a Set; memoising on the role keeps that out of
  // every render of every component that asks.
  return useMemo(() => (role === undefined ? NO_CAPABILITIES : capabilitiesFor([role])), [role]);
}

export function useCapability(capability: Capability): boolean {
  return useCapabilities().has(capability);
}
